import { apiClient } from './apiClient';

class BatchService {
    constructor() {
        this.BASE_URL = '/batches';
    }

    /**
     * Create a new batch
     */
    async createBatch(data) {
        return await apiClient.post(this.BASE_URL, data);
    }

    /**
     * Get batch by ID
     */
    async getBatch(id) {
        return await apiClient.get(`${this.BASE_URL}/${id}`);
    }

    /**
     * Get batch by claimId
     */
    async getBatchByClaimId(claimId) {
        return await apiClient.get(`${this.BASE_URL}/claim/${claimId}`);
    }

    /**
     * Update batch
     */
    async updateBatch(id, updates) {
        return await apiClient.put(`${this.BASE_URL}/${id}`, updates);
    }

    /**
     * Delete batch
     */
    async deleteBatch(id) {
        return await apiClient.delete(`${this.BASE_URL}/${id}`);
    }

    /**
     * Get all batches (with optional filters and pagination)
     */
    async getAllBatches(params = {}) {
        return await apiClient.get(this.BASE_URL, { params });
    }

    /**
     * Get batches by patient
     */
    async getBatchesByPatient(patientId) {
        return await apiClient.get(`${this.BASE_URL}/patient/${patientId}`);
    }

    /**
     * Add document to batch
     */
    async addDocumentToBatch(batchId, documentId, position) {
        return await apiClient.post(`${this.BASE_URL}/${batchId}/documents`, {
            documentId,
            position,
        });
    }

    /**
     * Remove document from batch
     */
    async removeDocumentFromBatch(batchId, documentId) {
        return await apiClient.delete(`${this.BASE_URL}/${batchId}/documents/${documentId}`);
    }

    /**
     * Reorder documents in batch
     */
    async reorderDocuments(batchId, documentIds) {
        return await apiClient.put(`${this.BASE_URL}/${batchId}/reorder`, {
            documentIds,
        });
    }

    /**
     * Consolidate batch into PDF
     */
    async consolidateBatch(batchId) {
        return await apiClient.post(`${this.BASE_URL}/${batchId}/consolidate`);
    }

    /**
     * Send batch
     */
    async sendBatch(batchId, payload = {}) {
        return await apiClient.post(`${this.BASE_URL}/${batchId}/send`, payload);
    }

    /**
     * Create super batch from multiple batches
     */
    async createSuperBatch(batchIds) {
        return await apiClient.post(`${this.BASE_URL}/super-consolidate`, { batchIds });
    }

    /**
     * Get all super batches with child batch details (supports filters and pagination)
     */
    async getSuperBatches(params = {}) {
        return await apiClient.get(`${this.BASE_URL}/super`, { params });
    }

    /**
     * Get batch history for a claim - returns all batches with their documents
     */
    async getBatchHistoryByClaimId(claimId) {
        return await apiClient.get(`${this.BASE_URL}/claim/${claimId}/history`);
    }

    /**
     * Get batch history for a patient
     */
    async getBatchHistoryByPatientId(patientId) {
        return await apiClient.get(`${this.BASE_URL}/patient/${patientId}/history`);
    }

    /**
     * Get batch history by insurance claim ID
     */
    async getBatchHistoryByInsuranceClaimId(insuranceClaimId) {
        return await apiClient.get(`${this.BASE_URL}/insurance/${insuranceClaimId}/history`);
    }

    /**
     * Get sent batches for a claim - for rebilling workflow
     */
    async getSentBatchesByClaimId(claimId) {
        return await apiClient.get(`${this.BASE_URL}/claim/${claimId}/sent`);
    }

    /**
     * Enqueue multiple batches for sequential processing
     */
    async enqueueBatches(batchIds) {
        return await apiClient.post(`${this.BASE_URL}/enqueue`, { batchIds });
    }

    /**
     * Get batch type counts (auto vs manual) for current month
     */
    async getBatchTypeCounts() {
        return await apiClient.get(`${this.BASE_URL}/counts/type-counts`);
    }

    /**
     * Get RX tracker data (monthly RX counts from sent batches)
     */
    async getRxTracker(months = 12) {
        return await apiClient.get(`${this.BASE_URL}/counts/rx-tracker?months=${months}`);
    }

    /**
     * Recompute RX tracker data for the last N months from sent batches
     */
    async syncRxTracker(months = 3) {
        return await apiClient.post(`${this.BASE_URL}/counts/rx-tracker/sync?months=${months}`);
    }
}

export const batchService = new BatchService();
